import React, { useEffect, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardAPI } from '../services/api';
import { FiBook, FiUsers, FiTrendingUp, FiUserCheck } from 'react-icons/fi';
import { BsCurrencyRupee } from 'react-icons/bs';
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await dashboardAPI.getDashboard();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-600 text-lg">Error loading dashboard</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-sm md:text-base text-gray-600">Welcome back! Here's what's happening with your institute.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        <StatCard
          title="Total Courses"
          value={stats.total_courses}
          icon={<FiBook />}
          color="#3b82f6"
          trend="+12%"
        />
        <StatCard
          title="Total Teachers"
          value={stats.total_teachers}
          icon={<FiUsers />}
          color="#10b981"
          trend="+5%"
        />
        <StatCard
          title="Total Students"
          value={stats.total_students}
          icon={<FiUserCheck />}
          color="#f59e0b"
          trend="+18%"
        />
        <StatCard
          title="Total Revenue"
          value={`₹${stats.total_revenue?.toLocaleString() || 0}`}
          icon={<BsCurrencyRupee />}
          color="#8b5cf6"
          trend="+24%"
        />
          <StatCard
          title="PlateForm Fee"
          value={`₹${stats.platform_fee?.toLocaleString() || 0}`}
          icon={<BsCurrencyRupee />}
          color="#8b5cf6"
          trend="+24%"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
        {/* Revenue Chart */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-2">
            <h3 className="text-base md:text-lg font-semibold text-gray-900">Monthly Revenue</h3>
            <span className="px-3 py-1 text-xs md:text-sm font-medium bg-green-100 text-green-800 rounded-full w-fit">
              ₹{stats.current_month_revenue?.toLocaleString() || 0} this month
            </span>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={stats.monthly_revenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Courses Chart */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
          <div className="mb-4 md:mb-6">
            <h3 className="text-base md:text-lg font-semibold text-gray-900">Top Performing Courses</h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.top_courses}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="title" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: '12px' }} />
              <Bar dataKey="sales_count" fill="#10b981" name="Sales" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Sales Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 md:p-6 border-b border-gray-200 gap-3">
          <h3 className="text-base md:text-lg font-semibold text-gray-900">Recent Course Sales</h3>
          <button className="px-4 py-2 text-xs md:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors w-fit">
            View All
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Amount</th>
                <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
           
              {stats.recent_sales && stats.recent_sales.length > 0 ? (
                stats.recent_sales.map((sale, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-medium text-gray-900">{sale.course_title}</td>
                    <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-600">{sale.student_name}</td>
                    <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-900">₹{sale.amount}</td>
                    <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-900">{sale.method}</td>
                    <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm font-medium text-green-600">₹{sale.institute_net_amount || sale.amount}</td>
                    <td className="px-3 md:px-6 py-3 md:py-4 text-xs md:text-sm text-gray-600">{new Date(sale.date).toLocaleDateString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-3 md:px-6 py-6 md:py-8 text-center text-sm text-gray-500">
                    No sales yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Institute Info */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4 md:mb-6">Institute Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div className="flex flex-col">
            <span className="text-xs md:text-sm text-gray-500 mb-2">Subscription Plan</span>
            <span className={`inline-block px-2 md:px-3 py-1 text-xs md:text-sm font-medium rounded-full w-fit ${getPlanColorClass(stats.institute?.subscription_plan)}`}>
              {stats.institute?.subscription_plan?.toUpperCase()}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs md:text-sm text-gray-500 mb-2">Max Teachers</span>
            <span className="text-base md:text-lg font-semibold text-gray-900">{stats.total_teachers} / {stats.institute?.max_teachers}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs md:text-sm text-gray-500 mb-2">Max Courses</span>
            <span className="text-base md:text-lg font-semibold text-gray-900">{stats.total_courses} / {stats.institute?.max_courses}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs md:text-sm text-gray-500 mb-2">Max Students</span>
            <span className="text-base md:text-lg font-semibold text-gray-900">{stats.total_students} / {stats.institute?.max_students}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, trend }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 md:p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs md:text-sm font-medium text-gray-600 mb-1 md:mb-2">{title}</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{value}</h2>
          {trend && (
            <div className="flex items-center text-xs md:text-sm text-green-600">
              <FiTrendingUp className="mr-1" size={14} />
              <span className="font-medium">{trend}</span>
            </div>
          )}
        </div>
        <div 
          className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-lg text-white text-lg md:text-xl flex-shrink-0"
          style={{ backgroundColor: color }}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};

// Helper function
const getPlanColorClass = (plan) => {
  switch (plan) {
    case 'free': return 'bg-blue-100 text-blue-800';
    case 'basic': return 'bg-yellow-100 text-yellow-800';
    case 'premium': return 'bg-green-100 text-green-800';
    case 'enterprise': return 'bg-purple-100 text-purple-800';
    default: return 'bg-blue-100 text-blue-800';
  }
};

export default Dashboard;

